"use client";

import { c as _c } from "react/compiler-runtime";
import { MenuItem } from "@/lib/types";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import AnimatedHamburger from "./AnimatedHamburger";
import AppButton from "../../shared/AppButton";
import NavMenuItem from "./NavMenuItem";
import SecondaryNavSection from "./SecondaryNavSection";
import { useCalBooking } from "@/providers/booking/CalBookingProvider";
import { caseStudyData } from "@/data/CaseStudyData";
interface NavigationOverlayProps {
  menuItems: MenuItem[];
  isOpen: boolean;
  onClose: () => void;
  revealPoint?: string; // The CSS circle position where the animation starts
}

// Controls the circular "zoom in/out" background animation
const overlayVariants = {
  closed: (point: string) => ({
    clipPath: point,
    // Starts as a tiny circle (0%) at the revealPoint
    transition: {
      type: "spring" as const,
      stiffness: 300,
      damping: 40,
      delay: 0.2
    }
  }),
  open: (point: string) => ({
    clipPath: point.replace("0%", "150%"),
    // Replaces 0% Circle with 150%
    transition: {
      type: "spring" as const,
      stiffness: 50,
      damping: 20
    }
  })
};

// Controls the "staggered" entry of the menu links (one after another)
const navListVariants = {
  closed: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  },
  open: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

// Controls the individual movement of each menu link
const navItemVariants = {
  closed: {
    y: 30,
    opacity: 0,
    transition: {
      duration: 0.2
    }
  },
  open: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1] as const
    }
  }
};

// Transforming raw case study data into the proper link format
const caseStudyLinks = caseStudyData.map(study => {
  // Convert to Lowercase and trim extra spaces
  return {
    label: study.title.trim().toLowerCase(),
    href: `/case-study/${study.slug}`,
    isBold: true
  };
});
const otherLinks = [{
  label: "Career",
  href: "/career"
}, {
  label: "Privacy Policy",
  href: "/privacy-policy"
}, {
  label: "Terms of Use",
  href: "/terms-of-use"
}];
const NavigationOverlay = t0 => {
  const $ = _c(13);
  const {
    menuItems,
    isOpen,
    onClose,
    revealPoint: t1
  } = t0;
  const revealPoint = t1 === undefined ? "circle(0% at calc(100% - 40px) 28px)" : t1;
  const {
    enabled: calEnabled,
    goToBooking
  } = useCalBooking();
  let t2;
  let t3;
  if ($[0] !== isOpen || $[1] !== onClose) {
    t2 = () => {
      const handleEsc = e => {
        if (e.key === "Escape" && isOpen) {
          onClose();
        }
      };
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    };
    t3 = [isOpen, onClose];
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== calEnabled || $[5] !== goToBooking || $[6] !== isOpen || $[7] !== menuItems || $[8] !== onClose || $[9] !== revealPoint) {
    t4 = isOpen && <motion.div className="fixed inset-0 bg-accent-foreground" style={{
      zIndex: 200
    }} initial={{
      clipPath: revealPoint
    }} animate={{
      clipPath: revealPoint.replace("0%", "150%")
    }} exit={{
      clipPath: revealPoint
    }} custom={revealPoint} variants={overlayVariants}><div className="absolute top-4 right-8 z-210"><AnimatedHamburger isOpen={isOpen} onClick={onClose} color="#b2d127" openColor="#b2d127" /></div><motion.div initial={{
        x: "100%"
      }} animate={{
        x: 0
      }} transition={{
        duration: 1,
        ease: [0.22, 1, 0.36, 1]
      }} className="lg:flex hidden absolute right-0 top-1/2 -translate-y-1/2 h-[180vh] aspect-square bg-white rounded-full translate-x-1/2 items-center justify-start z-0" /><div className="relative z-10 w-full h-full px-8 pt-16 pb-12 flex items-center justify-between"><div className="flex flex-col gap-8 lg:gap-12 max-w-xl"><motion.ul className="flex flex-col gap-1" variants={navListVariants} initial="closed" animate="open" exit="closed">{menuItems.map(item => <motion.div key={item.label} variants={navItemVariants}><NavMenuItem item={item} onNavigate={onClose} isOverlay={true} /></motion.div>)}<motion.li variants={navItemVariants} className="mt-1"><AppButton onClick={calEnabled ? () => {
                onClose();
                window.setTimeout(goToBooking, 450);
              } : onClose} href={calEnabled ? undefined : "/contact"} className="w-44" buttonClass="h-12 text-lg" shadowClass="-translate-x-1 translate-y-1"><span className="w-3 h-3 bg-black rounded-full" />Book Now</AppButton></motion.li></motion.ul><motion.div className="flex flex-col gap-2 text-white/40 text-xs uppercase tracking-widest pt-4" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }}><span>Octarnal Creative Studio</span><span className="normal-case">hi@octarnal.com</span></motion.div></div><div className="hidden lg:flex flex-col  w-[35%]"><SecondaryNavSection title="Case Studies" links={caseStudyLinks} onClose={onClose} /><SecondaryNavSection title="Other Links" links={otherLinks} onClose={onClose} /></div></div></motion.div>;
    $[4] = calEnabled;
    $[5] = goToBooking;
    $[6] = isOpen;
    $[7] = menuItems;
    $[8] = onClose;
    $[9] = revealPoint;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <AnimatePresence>{t4}</AnimatePresence>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
export default NavigationOverlay;